import { customInstanceWithHeader } from '@fukuri-admins/apis/client/base/axiosInstance';
import { getGetApiV1ThreeLetterCodePartnerAdminsClientsUrl } from '@fukuri-admins/apis/client/gen/endpoints';
import type { GetApiV1ThreeLetterCodePartnerAdminsClients200One } from '@fukuri-admins/apis/client/gen/model';
import { type UseFormReturn, useForm, useNavigate } from '@fukuri-admins/hooks/common';
import { useToast } from '@fukuri-admins/ui/common/Toast/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import { zodResolver } from '@hookform/resolvers/zod';
import { generateRequest } from '../logics/generateRequest';
import type { CreateUserFormFieldValues } from '../logics/schema';
import { userFormSchema } from '../logics/schema';
import type { CreateUserFormPresentation } from '../presentations';
import { usePostPartnerAdminsUsers } from './usePostPartnerAdminsUsers';

type PresentationProps = React.ComponentProps<typeof CreateUserFormPresentation>;

type IUseCreateUserForm = () => {
  state: {
    methods: UseFormReturn<CreateUserFormFieldValues>;
    isButtonDisabled: boolean;
    searchAction: PresentationProps['searchAction'];
  };
  actions: {
    handleOnSubmit: PresentationProps['onSubmit'];
  };
};

/**
 * 利用者追加フォームのカスタムフック
 *
 * @returns state.methods - react-hook-form のメソッド
 * @returns state.isButtonDisabled - ボタン非活性フラグ
 * @returns state.searchAction - 企業選択のページネーション対応検索関数
 * @returns actions.handleOnSubmit - フォーム送信ハンドラ
 */
export const useCreateUserForm: IUseCreateUserForm = () => {
  const threeLetterCode = getThreeLetterCode();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const methods = useForm<CreateUserFormFieldValues>({
    defaultValues: {
      clientId: '',
      fullName: '',
      email: '',
      externalUserId: '',
      password: '',
      passwordConfirmation: '',
      billableFrom: null,
    },
    criteriaMode: 'all',
    mode: 'onTouched',
    resolver: zodResolver(userFormSchema),
  });

  const { trigger, isMutating } = usePostPartnerAdminsUsers(threeLetterCode, {
    onSuccess: () => {
      showToast({
        message: '利用者を追加しました。',
        status: 'success',
      });
      navigate('/users');
    },
    onError: error => {
      const errors = error.response?.data?.errors;
      showToast({
        message: errors?.[0] ?? '利用者の追加に失敗しました。',
        status: 'danger',
      });
    },
  });

  const handleOnSubmit = methods.handleSubmit(formValues => {
    trigger(generateRequest({ formValues }));
  });

  // NOTE: PaginatedSelect は命令的 I/F（searchAction）を採用しているため、SWR hooks を経由せず
  // Orval fetcher を直接呼ぶのが現行の正規実装。SWR ベースへの刷新は Base UI Combobox への
  // 置き換え（LEA-3083）で対応予定。経緯は LEA-3082（キャンセル済み）を参照。
  /**
   * 利用企業選択の検索・ページネーション対応関数
   *
   * 企業名の部分一致検索と無限スクロール（ページネーション）を行う。
   */
  const searchAction: PresentationProps['searchAction'] = async (input, page) => {
    try {
      const url = getGetApiV1ThreeLetterCodePartnerAdminsClientsUrl(threeLetterCode, {
        ...(input && { name: input }),
        page,
        enabled: 1,
      });
      const res = await customInstanceWithHeader<GetApiV1ThreeLetterCodePartnerAdminsClients200One>(
        url,
        { method: 'GET' }
      );
      return {
        options: (res.data.clients ?? []).map(client => ({
          label: client.name,
          value: String(client.id),
        })),
        headers: { 'total-pages': res.headers['total-pages'] ?? '0' },
      };
    } catch {
      showToast({ message: 'データの取得に失敗しました。', status: 'danger' });
      return { options: [], headers: { 'total-pages': '0' } };
    }
  };

  return {
    state: {
      methods,
      isButtonDisabled: methods.formState.isSubmitting || !methods.formState.isValid || isMutating,
      searchAction,
    },
    actions: { handleOnSubmit },
  };
};
